import React, { useEffect, useRef, useState } from "react";
import CandidatePageShell from "./CandidatePageShell";
import useFetch from "../../useFetch";
import { getStoredUser, refreshStoredUser } from "../../auth";
import { apiRequest } from "../../utils/api";
import FormFeedback, { FieldError, inputErrorClass } from "../Common/FormFeedback";
import AccountReferralCard from "../Common/AccountReferralCard";
import "./Candidates.css";

const CandidateProfileSettings = () => {
  const formTopRef = useRef(null);
  const photoInputRef = useRef(null);
  const resumeInputRef = useRef(null);
  const storedUser = getStoredUser();

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [fullName, setFullName] = useState(storedUser?.name ?? "");
  const [professionalTitle, setProfessionalTitle] = useState("");
  const [email, setEmail] = useState(storedUser?.email ?? "");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [experience, setExperience] = useState("");
  const [expectedSalary, setExpectedSalary] = useState("");
  const [website, setWebsite] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [skills, setSkills] = useState("");
  const [bio, setBio] = useState("");
  const [photo, setPhoto] = useState(null);
  const [resume, setResume] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [resumeName, setResumeName] = useState("");

  const { response, isLoading: isLoadingFetch, error: fetchError } = useFetch(process.env.REACT_APP_BASE_API_URL + "/candidate-profile");
  const account = response?.account ?? storedUser;

  useEffect(() => {
    if (!response?.candidate_profile) return;

    const profile = response.candidate_profile;

    setFullName(profile.full_name ?? storedUser?.name ?? "");
    setProfessionalTitle(profile.professional_title ?? "");
    setEmail(profile.email ?? storedUser?.email ?? "");
    setPhone(profile.phone ?? "");
    setLocation(profile.location ?? "");
    setExperience(profile.experience ?? "");
    setExpectedSalary(profile.expected_salary ?? "");
    setWebsite(profile.website ?? "");
    setLinkedinUrl(profile.linkedin_url ?? "");
    setSkills(Array.isArray(profile.skills) ? profile.skills.join(", ") : "");
    setBio(profile.bio ?? "");
    setPhotoPreview(profile.photo_url ?? null);
    setResumeName(profile.resume_original_name ?? "");
  }, [response, storedUser?.email, storedUser?.name]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0] ?? null;

    setPhoto(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : response?.candidate_profile?.photo_url ?? null);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0] ?? null;

    setResume(file);
    setResumeName(file ? file.name : response?.candidate_profile?.resume_original_name ?? "");
  };

  const updateStoredUser = (profile) => {
    const currentUser = getStoredUser();

    if (!currentUser) return;

    refreshStoredUser({
      ...currentUser,
      name: profile.full_name ?? currentUser.name,
      image_url: profile.photo_url ?? null,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);
    setSuccess(false);
    setError(false);
    setValidationErrors([]);
    setFieldErrors({});

    let formData = new FormData();
    formData.append("full_name", fullName);
    formData.append("professional_title", professionalTitle);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("location", location);
    formData.append("experience", experience);
    formData.append("expected_salary", expectedSalary);
    formData.append("website", website);
    formData.append("linkedin_url", linkedinUrl);
    formData.append("skills", skills);
    formData.append("bio", bio);
    if (photo) formData.append("photo", photo);
    if (resume) formData.append("resume", resume);

    apiRequest("/candidate-profile", {
      method: "POST",
      body: formData,
    })
      .then(data => {
        formTopRef.current?.scrollIntoView();
        setIsLoading(false);

        if (data?.status === "success") {
          setSuccess(data.message);
          setPhoto(null);
          setResume(null);
          setPhotoPreview(data.candidate_profile?.photo_url ?? null);
          setResumeName(data.candidate_profile?.resume_original_name ?? "");
          updateStoredUser(data.candidate_profile);
        }
        else if (data?.status === "validation_error") {
          setValidationErrors(data.messages);
          setFieldErrors(data.errors ?? {});
        }
        else if (data?.status === "error" && data?.message) {
          setError(data.message);
        }
        else {
          setError("There was some error while saving candidate profile, please try again.");
        }
      })
      .catch(error => {
        formTopRef.current?.scrollIntoView();
        setIsLoading(false);
        if (error.data?.status === "validation_error") {
          setValidationErrors(error.messages);
          setFieldErrors(error.errors);
        }
        else {
          setError(error.message);
        }
      });
  };

  return (
    <CandidatePageShell
      active="profile"
      title="My Profile"
      text="Keep the basic details employers need to understand your profile."
    >
      <div className="row">
        <div className="col-lg-12">
          <div className="ls-widget candidate-profile-settings">
            <div ref={formTopRef}></div>
            <div className="tabs-box">
              <div className="widget-title">
                <h4>Profile Settings</h4>
              </div>

              <div className="widget-content">
                <FormFeedback success={success} error={error} validationErrors={validationErrors} />
                {fetchError && <div className="alert alert-danger"><strong>{fetchError}</strong></div>}
                {isLoadingFetch && <h5 className="text-center my-3">Loading Data</h5>}
                <AccountReferralCard account={account} className="profile-account-referral" />

                <form className="default-form" onSubmit={handleSubmit}>
                  <div className="uploading-outer candidate-upload-box candidate-upload-card">
                    <div className="candidate-upload-preview">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Candidate preview" />
                      ) : (
                        <span className="la la-user"></span>
                      )}
                    </div>
                    <div className="candidate-upload-content">
                      <strong>{photo ? photo.name : photoPreview ? "Photo uploaded" : "Upload profile photo"}</strong>
                      <span>Use a clear profile photo. JPG or PNG, max 2MB.</span>
                      <button type="button" className="theme-btn btn-style-three" onClick={() => photoInputRef.current?.click()}>
                        Choose Photo
                      </button>
                    </div>
                    <input ref={photoInputRef} className="uploadButton-input" type="file" accept="image/*" onChange={handlePhotoChange} />
                  </div>

                  <div className="row">
                    <div className="form-group col-lg-6 col-md-12">
                      <label>Full Name</label>
                      <input type="text" className={inputErrorClass(fieldErrors, "full_name")} placeholder="Jerome Bell" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
                      <FieldError errors={fieldErrors} name="full_name" />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Professional Title</label>
                      <input type="text" placeholder="Frontend Developer" value={professionalTitle} onChange={(e) => setProfessionalTitle(e.target.value)} />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Email Address</label>
                      <input type="email" className={inputErrorClass(fieldErrors, "email")} placeholder="jerome@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                      <FieldError errors={fieldErrors} name="email" />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Phone</label>
                      <input type="text" placeholder="+1 234 567 890" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Location</label>
                      <input type="text" placeholder="London, UK" value={location} onChange={(e) => setLocation(e.target.value)} />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Experience</label>
                      <input type="text" placeholder="4 years" value={experience} onChange={(e) => setExperience(e.target.value)} />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Expected Salary</label>
                      <input type="text" placeholder="$70k - $90k" value={expectedSalary} onChange={(e) => setExpectedSalary(e.target.value)} />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Skills</label>
                      <input type="text" placeholder="React, JavaScript, UI Design" value={skills} onChange={(e) => setSkills(e.target.value)} />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>Portfolio Website</label>
                      <input type="url" className={inputErrorClass(fieldErrors, "website")} placeholder="https://yourportfolio.com" value={website} onChange={(e) => setWebsite(e.target.value)} />
                      <FieldError errors={fieldErrors} name="website" />
                    </div>

                    <div className="form-group col-lg-6 col-md-12">
                      <label>LinkedIn URL</label>
                      <input type="url" className={inputErrorClass(fieldErrors, "linkedin_url")} placeholder="https://linkedin.com/in/username" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} />
                      <FieldError errors={fieldErrors} name="linkedin_url" />
                    </div>

                    <div className="form-group col-lg-12 col-md-12">
                      <label>Short Bio</label>
                      <textarea placeholder="Write a short summary about your experience, strengths, and preferred job type." value={bio} onChange={(e) => setBio(e.target.value)}></textarea>
                    </div>

                    <div className="form-group col-lg-12 col-md-12">
                      <label>Resume</label>
                      <div className="uploading-outer candidate-upload-box">
                        <div className="uploadButton">
                          <input ref={resumeInputRef} className="uploadButton-input" type="file" id="candidate-resume" accept=".pdf,.doc,.docx" onChange={handleResumeChange} />
                          <label className="uploadButton-button ripple-effect" htmlFor="candidate-resume">Upload Resume</label>
                        </div>
                      <div className="text">{resumeName || "PDF, DOC, or DOCX file recommended."}</div>
                      </div>
                      <FieldError errors={fieldErrors} name="resume" />
                    </div>

                    <div className="form-group col-lg-12 col-md-12">
                      <button type="submit" className="theme-btn btn-style-one" disabled={isLoading}>
                        {isLoading ? "Loading..." : "Save Profile"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CandidatePageShell>
  );
};

export default CandidateProfileSettings;
